import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiRequests } from '../../shared/api/apiRequests';
import { formatImageUrl } from '../../shared/utils/formatImageUrl';
import { dateToString } from '../../shared/utils/parseDate.js';
import { Pagination } from '../../features/Pagination/index.js';
import { useUrlPagination } from '../../shared/hooks/useUrlPagination.js';
import { PageBar } from '../../shared/ui/PageBar';
import { EntityCard } from '../../shared/ui/EntityCard';
import { EmptyState } from '../../shared/ui/EmptyState';
import { Megaphone, Plus } from 'lucide-react';

const LIST_PATH = '/others/news';

const PAGE_SIZE = 10;

const NewsPage = () => {
  const [news, setNews] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const [searchValue, setSearchValue] = useState(q);
  const [reloadKey, setReloadKey] = useState(0);
  const { page, offset, pageSize, setPage } = useUrlPagination(PAGE_SIZE);

  // Черновик в поле поиска следует за URL (назад/вперёд в браузере)
  useEffect(() => {
    setSearchValue(q);
  }, [q]);

  useEffect(() => {
    let cancelled = false;
    const request = q
      ? apiRequests.news.getFiltered(q, pageSize, offset)
      : apiRequests.news.get(pageSize, offset);
    request
      .then((res) => {
        if (!cancelled) setNews(res);
      })
      .catch((e) => console.error(e));
    return () => {
      cancelled = true;
    };
  }, [q, offset, pageSize, reloadKey]);

  const handleSearch = () => {
    const next = searchValue.trim();
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next) params.set('q', next);
      else params.delete('q');
      params.delete('page');
      return params;
    });
  };

  const handleDelete = (id) => {
    apiRequests.news.delete(id).then(() => {
      if (news.items?.length === 1 && page > 1) setPage(page - 1);
      else setReloadKey((k) => k + 1);
    });
  };

  return (
    <>
      <PageBar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onSearch={handleSearch}
        searchPlaceholder="Поиск новостей..."
        createLink={`${LIST_PATH}/create`}
      />

      <div className="mb-3">
        {news.items?.length === 0 ? (
          q ? (
            <EmptyState
              icon={Megaphone}
              variant="search"
              title="Публикации не найдены"
              description={`Среди публикаций нет совпадений с «${q}». Попробуйте искать по названию издания или ключевому слову из заголовка.`}
            />
          ) : (
            <EmptyState
              icon={Megaphone}
              title="Публикаций о нас пока нет"
              description="Сюда собираются статьи и интервью о компании в СМИ. Добавьте первую — достаточно ссылки, заголовка и картинки."
              action={{ label: 'Добавить публикацию', to: `${LIST_PATH}/create`, icon: Plus }}
            />
          )
        ) : (
          news.items?.map((item) => (
            <EntityCard
              key={item.id}
              image={formatImageUrl(item.imgPath)}
              title={item.title}
              fields={[{ label: 'Создано', value: dateToString(item.createdAt) }]}
              editLink={`${LIST_PATH}/${item.id}`}
              onDelete={() => handleDelete(item.id)}
            />
          ))
        )}
      </div>

      <Pagination page={page} total={news.totalCount} pageSize={pageSize} onPageChange={setPage} />
    </>
  );
};

export default NewsPage;
